import { useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { cx } from '../charte';
import type { Section } from '../../store/ui';
import { Sheet } from './Sheet';
import { bottomTabs, unreadLabel, type NavItem } from './nav';

function Count({ value, className }: { value: number; className?: string }) {
  if (value <= 0) return null;
  return (
    <span className={cx('grid h-4 min-w-[16px] place-items-center rounded-pill bg-danger px-1 text-[10px] font-bold leading-none text-white', className)}>
      <span aria-hidden="true">{value > 99 ? '99+' : value}</span>
      <span className="sr-only">{unreadLabel(value)}</span>
    </span>
  );
}

interface NavProps {
  items: NavItem[];
  section: Section;
  onSection: (s: Section) => void;
  unreadMessages: number;
}

/**
 * Colonne de gauche (tablette et ordinateur) : étroite jusqu'à 1 280 px (icône sur libellé, au pouce en
 * paysage), large au-delà (logo complet, icône à côté du libellé).
 */
export function SideNav({ items, section, onSection, unreadMessages }: NavProps) {
  return (
    <aside className="fixed inset-y-0 left-0 z-40 hidden w-[88px] flex-col border-r border-line bg-white md:flex xl:w-[232px]">
      <div className="flex h-16 shrink-0 items-center justify-center px-4 xl:justify-start xl:px-6">
        <img src="/favicon.png" alt="Permio" className="h-9 w-9 xl:hidden" />
        <img src="/logo-permio-moniteur.png" alt="Permio" className="hidden h-7 w-auto xl:block" />
      </div>
      <nav aria-label="Navigation principale" className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-2 pb-4 pt-2 xl:px-3">
        {items.map(({ id, label, title, icon: Icon }) => {
          const active = section === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onSection(id)}
              aria-current={active ? 'page' : undefined}
              title={title}
              className={cx(
                'group relative flex flex-col items-center gap-1 rounded-row px-1 py-2 text-[11px] font-medium transition-colors',
                'xl:flex-row xl:gap-3 xl:px-3 xl:py-2.5 xl:text-sm',
                active ? 'text-primary-600 xl:bg-primary-50' : 'text-secondary hover:text-body xl:hover:bg-surface-light',
              )}
            >
              <span
                className={cx(
                  'relative grid h-9 w-14 place-items-center rounded-pill transition-colors xl:h-auto xl:w-auto',
                  active ? 'bg-primary-50 xl:bg-transparent' : 'group-hover:bg-surface-light xl:group-hover:bg-transparent',
                )}
              >
                <Icon size={20} strokeWidth={active ? 2.2 : 1.8} aria-hidden />
                {id === 'messages' && <Count value={unreadMessages} className="absolute -top-1 right-1.5 xl:hidden" />}
              </span>
              <span className="max-w-full truncate">{label}</span>
              {id === 'messages' && <Count value={unreadMessages} className="ml-auto hidden xl:grid" />}
            </button>
          );
        })}
      </nav>
    </aside>
  );
}

/** Barre fixe en bas (téléphone) : Journée, Planning, Élèves, Messages et « Plus ». */
export function BottomBar({ items, section, onSection, unreadMessages }: NavProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const { main, more } = bottomTabs(items);
  const moreActive = more.some((m) => m.id === section);
  const cell = (active: boolean) =>
    cx(
      'relative flex min-w-0 flex-1 flex-col items-center justify-center gap-1 pt-2 pb-1.5 text-[11px] font-medium transition-colors',
      active ? 'text-primary-600' : 'text-secondary hover:text-body',
    );
  const pill = (active: boolean) => cx('grid h-8 w-14 place-items-center rounded-pill transition-colors', active && 'bg-primary-50');
  return (
    <>
      <nav
        aria-label="Navigation principale"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-white/95 pb-[env(safe-area-inset-bottom)] shadow-[0_-8px_24px_-12px_rgba(15,23,42,0.12)] backdrop-blur md:hidden"
      >
        <div className="mx-auto flex max-w-xl">
          {main.map(({ id, label, icon: Icon }) => {
            const active = section === id;
            return (
              <button key={id} type="button" onClick={() => onSection(id)} aria-current={active ? 'page' : undefined} className={cell(active)}>
                <span className={pill(active)}>
                  <Icon size={20} strokeWidth={active ? 2.2 : 1.8} aria-hidden />
                </span>
                <span className="flex max-w-full items-center gap-1 truncate">
                  {label}
                  {id === 'messages' && <Count value={unreadMessages} className="absolute right-[calc(50%-24px)] top-1.5" />}
                </span>
              </button>
            );
          })}
          {more.length > 0 && (
            <button type="button" onClick={() => setMoreOpen(true)} aria-haspopup="dialog" className={cell(moreActive)}>
              <span className={pill(moreActive)}>
                <MoreHorizontal size={20} aria-hidden />
              </span>
              Plus
            </button>
          )}
        </div>
      </nav>
      {moreOpen && (
        <Sheet title="Plus" onClose={() => setMoreOpen(false)}>
          <ul className="grid gap-1">
            {more.map(({ id, title, icon: Icon }) => {
              const active = section === id;
              return (
                <li key={id}>
                  <button
                    type="button"
                    onClick={() => {
                      onSection(id);
                      setMoreOpen(false);
                    }}
                    aria-current={active ? 'page' : undefined}
                    className={cx(
                      'flex w-full items-center gap-3 rounded-row px-3 py-3 text-left text-[15px] font-medium transition-colors',
                      active ? 'bg-primary-50 text-primary-700' : 'text-body hover:bg-surface-light',
                    )}
                  >
                    <span className={cx('grid h-9 w-9 place-items-center rounded-xl', active ? 'bg-white text-primary' : 'bg-surface-indigo text-primary')}>
                      <Icon size={18} aria-hidden />
                    </span>
                    {title}
                  </button>
                </li>
              );
            })}
          </ul>
        </Sheet>
      )}
    </>
  );
}
